import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';

const StyledContactSection = styled.section`
  max-width: 1200px;
  margin: 0 auto;
  text-align: left;

  .intro {
    max-width: 900px;
    margin-bottom: 40px;
    .subtle {
    font-size: var(--fz);
    color: var(--light-slate);
    font-weight: normal;
    margin-left: 6px;
  }
  }

  .people-list {
    display: flex;
    flex-direction: column;
    gap: 48px;
    margin-top: 30px;
  }

  .people-column {
    max-width: 1200px;

    h3 {
      margin-bottom: 12px;
      font-size: var(--fz-lg);
      color: var(--lightest-slate);
    }

    ul {
      list-style: none;
      padding: 0;
      margin: 0;
    }

    li {
      margin-bottom: 8px;
      font-size: var(--fz-sm);
      color: var(--slate);
      font-family: var(--font-sans);
      line-height: 1.5;
    }
      h3 {
    margin-bottom: 12px;
    font-size: var(--fz-lg);
    color: var(--lightest-slate);

    .subtle {
      font-size: var(--fz-xs);
      color: var(--light-slate);
      font-weight: normal;
      margin-left: 6px;
    }
  }
  }

  .people-column:not(:last-child) {
    padding-bottom: 32px;
    border-bottom: 1px solid var(--lightest-navy);
  }
`;

const Contact = () => {
  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) return;
    sr.reveal(revealContainer.current, srConfig());
  }, [prefersReducedMotion]);

  return (
    <StyledContactSection id="contact" ref={revealContainer}>
      <h2 className="numbered-heading">Mentors, Mates, Mentees</h2>

      <div className="intro">
        <p>
          I’m incredibly lucky to work with and learn from generous mentors, collaborators, and
          trainees. I’m slowly collecting names here as a way to say thank you and to recognize
          the people who shape my work and thinking. Although these senior collaborators do not formally supervise me, I regard them as
          scientific and professional mentors in the broader scholarly sense, and I deeply value
          their guidance and perspective. This reflects a belief I hold closely:{' '}
          <em>闻道有先后，术业有专攻</em> — those who come earlier to understanding, or who possess
          distinct expertise, can be teachers. <span className="subtle">(I am still working on adding the names...manually)</span> </p>
      </div>

      <div className="people-list">
        <div className="people-column">
          <h3>Mentors <span className="subtle">(descending time)</span></h3>
          <ul>
            <li>Samuel L. Volchenboum, MD, PhD — (Role: Chief, Supervisor) Professor; Dean of Master's Education, University of Chicago</li>
            <li>Kun-Hsing Yu, MD, PhD — (Role: Postdoc advisor) Associate Professor, Harvard Medical School</li>
            <li>MacLean Nasrallah, MD, PhD — (Role: Senior collaborator) Associate Professor, Associate Vice Chair for Research, Hospital of the University of Pennsylvania</li>
            <li>Jeffrey A. Golden, MD — (Role: Senior collaborator) Professor, Vice dean of Research and Graduate Education, Cedars-Sinai</li>
            <li>Nima Saeidi, PhD — (Role: Senior collaborator) Associate Professor, Department of Surgery, Mass General Brigham - Harvard Medical School</li>
            <li>Victor Y. Chen, PhD — (Role: Ph.D. Committee chair) Professor; Associate School Head, Purdue University</li>
            <li>David Ebert, PhD — (Role: Ph.D. Committee) Professor; IEEE Fellow; Associate Vice President, University of Arizona</li>
            <li>Baijian Yang, PhD — (Role: Ph.D. Committee) Professor; Associate Dean, Purdue University</li>
            <li>James L. Mohler, PhD — (Role: Ph.D. Committee) Professor; Associate Vice President, Purdue University</li>
            <li>Panpan Xu, PhD — (Role: Intership mentor); Principal Applied Scientist, AWS AI/ML</li>
          </ul>
        </div>

        <div className="people-column">
          <h3>Mates</h3>
          <ul>
            <li>Xiyue Wang, PhD — Stanford University</li>
            <li>Sen Yang — Ant Group</li>
            <li>Christopher Jackson, MD, PhD — Assistant Professor, Department of Pathology and Laboratory Medicine, Penn State</li>
            <li>David Cipoletta — Chief Executive Officer, Pison </li>
          </ul>
        </div>

        <div className="people-column">
          <h3>Mentees <span className="subtle"> [evidenced by my formal signatures on their paperwork or reference letters]</span></h3>
          <ul>
            <li>Liza Mathews - Harvard Medical School - Master Capstone - Current: Medical student, George Washington University</li>
            <li>Guillaume Larghero - ETH Zürich - Master Thesis - Current: PhD student, Harvard Medical School</li>
            <li>Irene Gao  - Noble and Greenough School (MA, USA) - Regeneron STS Scholar '24 - Current: Undergraduate student, Columbia University</li>
            <li>Kimi Kwan Yeung Liu  - St Paul's School (London, UK) - Finalist of UK Big Bang Competition - Current: Undergraduate student, University of Chicago</li>
            <li>Justin Kaicheng U  - Cornell University - Undergraduate research - Current: PhD student (Advised by Nobel Laureate Charles Rice), Tri-I Weill Cornell Medicine</li>
            <li>Sophia Meixuan Zhang  - Cornell University - Undergraduate research - Current: Research associate, The Nai-Kong Cheung Lab, Memorial Sloan Kettering Cancer Center</li>
            <li>Dhruv Rana - Virgina Tech - Undergraduate research - Current: Undergraduate student, New York University</li>
          </ul>
        </div>
      </div>
    </StyledContactSection>
  );
};

export default Contact;
